import { useId } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useAutosave } from "./useDocument";

interface Common {
  label: string;
  value: string;
  save: (value: string) => Promise<unknown>;
  className?: string;
  hint?: React.ReactNode;
}

function Shell({ id, label, className, hint, error, children }: { id: string; label: string; className?: string; hint?: React.ReactNode; error: string | null; children: React.ReactNode }) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-[0.8125rem] font-medium">{label}</label>
      {children}
      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : hint}
    </div>
  );
}

/** A labelled input that saves itself (600 ms after typing, or on blur). */
export function SavedInput({
  label, value, save, className, hint, suffix, ...input
}: Common & { suffix?: string } & Omit<React.ComponentProps<"input">, "value" | "onChange" | "onBlur">) {
  const id = useId();
  const f = useAutosave(value, save);
  const field = (
    <Input
      id={id}
      {...input}
      value={f.value}
      aria-invalid={f.error !== null || undefined}
      aria-describedby={suffix ? `${id}-suffix` : undefined}
      onChange={(e) => f.change(e.target.value)}
      onFocus={f.focus}
      onBlur={f.blur}
      className={cn("pointer-coarse:h-11", input.type === "date" && "tabular-nums", suffix && "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none")}
    />
  );
  return (
    <Shell id={id} label={label} className={className} hint={hint} error={f.error}>
      {suffix ? (
        <div className="relative">
          {field}
          {/* Reads as the field's continuation: „14“ „Tage · bis 17.11.“ */}
          <span id={`${id}-suffix`} className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">{suffix}</span>
        </div>
      ) : field}
    </Shell>
  );
}

export function SavedTextarea({ label, value, save, className, hint, rows = 3 }: Common & { rows?: number }) {
  const id = useId();
  const f = useAutosave(value, save);
  return (
    <Shell id={id} label={label} className={className} hint={hint} error={f.error}>
      <Textarea
        id={id}
        rows={rows}
        value={f.value}
        aria-invalid={f.error !== null || undefined}
        onChange={(e) => f.change(e.target.value)}
        onFocus={f.focus}
        onBlur={f.blur}
        className="leading-normal"
      />
    </Shell>
  );
}
